/* Plugin registry.
 *
 * A plugin is a connection to an outside system. Where a *module* (apps.js) adds
 * a page to the console, a plugin adds a capability to a module that is already
 * there — and can be switched off, at which point the app must behave as though
 * that system never existed.
 *
 * Plugins are grouped into SLOTS by kind. A slot holds every plugin registered
 * for that kind and remembers which one is switched on, so swapping Mangomint
 * for another booking system is picking a different plugin in the same slot, not
 * a rewrite. Today there are two slots:
 *
 *   scheduling   where appointments, the service catalogue and checkout times
 *                come from. See the provider contract below.
 *
 *   channel      an outside source of bookings that are NOT in the book —
 *                a marketplace the salon takes work from but which does not
 *                write to its calendar. See the second contract below.
 *
 * A kind can declare `requires: ['otherKind']`. The slot is then off, and says
 * why, whenever what it depends on is off — `channel` needs `scheduling`,
 * because a booking that should be on the book means nothing without a book.
 *
 * A kind also names the `licence` feature it belongs to. An unlicensed slot is
 * off in exactly the way an unwanted one is — the store checks `enabled` and
 * `can()` in forty places and none of them learns a new condition — and the
 * settings card says which of the two it is, because "we turned this off" and
 * "you have not bought this" are different sentences and only one of them has a
 * switch that will help. See js/licensing.js, which answers `true` to
 * everything whenever it does not know, so a console with no licensing schema
 * behind it behaves exactly as it always did.
 *
 * A plugin registers itself once its dependencies exist:
 *
 *   import { plugins } from '../plugins.js';
 *
 *   plugins.register({
 *     id: 'mangomint',
 *     kind: 'scheduling',
 *     name: 'Mangomint',
 *     blurb: 'One line for the settings card.',
 *     capabilities: { catalogue: true, checkout: true, realtime: true },
 *     provider: { ...the contract... }
 *   });
 *
 * ---------------------------------------------------------------------------
 * The `scheduling` provider contract
 * ---------------------------------------------------------------------------
 * Every method is async unless noted. The store never sees a vendor's tables,
 * ids or time formats — normalising those is the provider's whole job.
 *
 *   fetchAppointments(dateKey) -> [{
 *     id, clientId, startsAt (ms), notes, isFirst, cancelled, clientName,
 *     services: [{ id, serviceId, name, startsAt (ms), durationMinutes, price, techs: [],
 *                  options: [{ id, name, groupId, groupName }] }]
 *   }]
 *     `serviceId` is the OUTSIDE system's service id — what the mapping is keyed
 *     on. `techs` are display labels ("Ann D."); the store matches them against
 *     roster first names. Times are local-clock milliseconds, already corrected
 *     for whatever the source stores.
 *     `clientId` is who the appointment is for, and it is NOT an identity: a
 *     placeholder client ("Walk In") is one record shared by many people. The
 *     store keys every match on the appointment `id`, never on `clientId` or
 *     `clientName`. See js/rules/visit-key.js.
 *     `price` is what the line costs with its options, in dollars, or null
 *     when the source does not say. It decides full turn or half — see
 *     js/rules/price-weight.js.
 *     `services[].id` need not survive an edit to the appointment — a provider
 *     whose source has no stable line ids will hand back new ones — so the store
 *     re-finds a booked service by appointment + `serviceId` when it has to.
 *
 *   fetchOptionCatalogue() -> [{ id, name, groupId, groupName }]     [options]
 *     Every option a booking can carry, for the rules screen.
 *   fetchOptionRules() -> { optionId: { optionId, optionName, groupId, groupName,
 *                                       serviceId, staffIds: [] } }
 *     What each option does to a turn: the type it becomes, and the technicians
 *     allowed to take it.
 *   saveOptionRule(option, rule)   rule empty = forget it. sync.
 *   clearOptionRules(optionIds)    forget all of them. sync.
 *
 *   fetchClosedTickets(dateKey) -> { appointmentId: closedAtMs }   [checkout]
 *     When a client is checked out. Only appointments that are settled.
 *
 *   fetchBookedRequests(dateKey) -> [{ appointmentId, serviceId, startsAt,
 *                                      staffId, type, additionalStaffId,
 *                                      additionalType }]           [requests]
 *     Who the booking system has each booked service requested of, where it
 *     knows. `type` is the source's own word — the store treats 'SpecificStaff'
 *     as a request and anything else as the source saying it is not one — and
 *     staff ids are the source's, set against `external_staff_id`. A service
 *     with no entry is one the source has not reported on; the desk's own mark
 *     is what counts there. The book's `services[]` carry `appointmentId` so a
 *     line can be matched to its entry.
 *
 *   fetchBookStatus(dateKey) -> { appointmentId: { status, waitingSince,
 *                                checkedInAt } }               [waitingRoom]
 *     Where each appointment stands in the booking system's own workflow.
 *     'Waiting' holds a square put up for it until the client is checked in;
 *     the store starts it from `checkedInAt` once the status moves past it.
 *
 *   fetchWorkHours(dateKey) -> { sourceStaffId: { spans: [{ from, to }],
 *                                blocks: [{ id, from, to, label }],
 *                                changedAt } }                 [workHours]
 *     Who the booking system has working today, when, and the time blocked
 *     off in between, keyed by the source's staff id (set against
 *     `external_staff_id`). `changedAt` is when that person's hours last
 *     changed. The rules for what the board does with it are
 *     js/rules/work-hours.js; somebody with no entry is the desk's alone.
 *
 *   fetchInService(dateKey) -> { appointmentId: inServiceAtMs }    [inService]
 *     When a client was seated — the other end of the service from a checkout.
 *     Only appointments actually under way. Declaring this capability is what
 *     lets the board start turns off the book rather than inferring the moment
 *     from the first square somebody puts up.
 *
 *   fetchCatalogue() -> { catalogue: [{ id, name }], map: { serviceId: ttServiceId } }
 *                                                                  [catalogue]
 *   fetchServiceMap() -> { serviceId: ttServiceId }
 *   saveServiceMapping(serviceId, ttServiceId)   ttServiceId null = unmap. sync.
 *   clearServiceMap(serviceIds)                  forget all of them. sync.
 *
 *   subscribe(onChange, onJoined) -> handle                        [realtime]
 *   unsubscribe(handle)
 *     onChange is called with a SEMANTIC kind, never a table name: 'book' when
 *     the day's appointments changed, 'checkout' when a ticket settled,
 *     'requests' when who a booking was asked for changed, 'status' when an
 *     appointment moved through the waiting room, 'shifts' when somebody's
 *     hours or time blocks changed.
 *
 * ---------------------------------------------------------------------------
 * The `channel` provider contract
 * ---------------------------------------------------------------------------
 * A channel knows what SHOULD be on the book. It never reads the book itself —
 * that is the scheduling plugin's job, and duplicating it here would mean a new
 * booking system needed a new channel plugin too.
 *
 *   fetchReservations(fromDateKey, toDateKey) -> [{
 *     externalId, status ('booked' | 'cancelled'), clientName, clientEmail,
 *     serviceName, startsAt (ms), durationMinutes, updatedAt (ms)
 *   }]
 *     `externalId` is the channel's own id for the reservation and is what a
 *     cancellation is matched to its booking on. Times are local-clock
 *     milliseconds, like everything else the store handles.
 *
 *   status() -> { installed, schemaVersion, config, runs, unknownSubjects }
 *   saveConfig(fields)   sync — through the server, never straight to a table
 *   test(fields)         log in and let go, so a bad password fails in front of
 *                        whoever typed it
 *   pollNow()            check the source now rather than at the next schedule
 *   forget()             drop the credentials entirely
 *   subscribe(onChange, onJoined) -> handle                        [realtime]
 *   unsubscribe(handle)
 *
 * Capabilities are declared, not inferred: the settings card lists them, and the
 * store checks `can('checkout')` before offering to stop turns at checkout, so a
 * provider that only knows about bookings simply never shows that switch.
 */
import * as Vue from 'vue';
import { licensing } from './licensing.js';
// Aliased: `prefs` below is this file's own reactive plugin state, which is a
// different thing from the salon's settings and was only ever distinguishable
// as a global. The settings are where that state is persisted to.
import { prefs as settings } from './prefs.js';

var KINDS = {
  scheduling: {
    label: 'Appointments',
    // On by default so an install that has a booking system connected behaves
    // as it always has; switching it off is the deliberate act.
    enabled: true,
    // The licence feature this slot belongs to. Not licensed means off, and
    // the salon's own switch cannot turn it back on.
    licence: 'appointments'
  },
  channel: {
    label: 'Booking channels',
    licence: 'channel',
    // Off by default, unlike scheduling: an install that has never heard of
    // an outside channel should not have one half-configured on the settings
    // page, and there is nothing for it to do until somebody gives it a
    // mailbox.
    enabled: false,
    // A channel is a source of bookings that are NOT in the book, which only
    // means anything if there is a book to compare them against. With no
    // scheduling plugin on, this slot has nothing to say and says nothing —
    // see `blocked` below.
    requires: ['scheduling'],
    // Disabled: ClassPass was the only channel, and it is now synced into
    // Mangomint outside this repo. No plugin registers here and settings has
    // no Channels tab, so the slot is always off. Kept so the contract above
    // is still here if a channel is ever wanted again.
    disabled: true
  }
};

var registry = Vue.reactive([]);
var prefs = Vue.reactive(load());

/* Which booking system this console talks to is the salon's answer, not the
 * screen's, so it lives in the shared settings — see js/prefs.js. Every field
 * is checked on the way in: this is a value another station wrote, possibly a
 * build ago, and a bad one here would silently switch the plugin off. */
function load() {
  var out = {};
  Object.keys(KINDS).forEach(function (kind) {
    out[kind] = { enabled: KINDS[kind].enabled, pluginId: null };
  });
  var saved = settings.get('plugins');
  if (saved && typeof saved === 'object') {
    Object.keys(out).forEach(function (kind) {
      var s = saved[kind];
      if (!s || typeof s !== 'object') return;
      if (typeof s.enabled === 'boolean') out[kind].enabled = s.enabled;
      if (typeof s.pluginId === 'string') out[kind].pluginId = s.pluginId;
    });
  }
  return out;
}

function save() {
  var out = {};
  Object.keys(prefs).forEach(function (kind) {
    out[kind] = { enabled: prefs[kind].enabled, pluginId: prefs[kind].pluginId };
  });
  settings.set('plugins', out);
}

// The settings arrived from the server, or another station changed them. Same
// parsing as the first load, applied onto the reactive object rather than
// replacing it, so everything already watching it follows.
function reload() {
  var fresh = load();
  Object.keys(fresh).forEach(function (kind) {
    prefs[kind].enabled = fresh[kind].enabled;
    prefs[kind].pluginId = fresh[kind].pluginId;
  });
}

function get(id) {
  return registry.find(function (p) { return p.id === id; }) || null;
}

function forKind(kind) {
  return registry.filter(function (p) { return p.kind === kind; });
}

function register(plugin) {
  if (!plugin || !plugin.id || !KINDS[plugin.kind]) return;
  if (get(plugin.id)) return;
  // `settings` is the name of a globally registered component that draws this
  // plugin's own configuration. Declaring it is how a plugin gets a form on
  // the settings page without settings.js having to know what it configures.
  registry.push(Object.assign({ capabilities: {}, blurb: '', settings: null }, plugin));
  registry.sort(function (a, b) { return a.name.localeCompare(b.name); });
}

var slots = {};

/* Which of a kind's required slots are not switched on.
 *
 * Read live rather than resolved once: the answer changes the moment somebody
 * toggles the slot it depends on, and a dependent slot that stayed on after
 * its dependency went off would be offering a comparison against a book that
 * is no longer there. Empty array means nothing is in the way.
 */
function unmetRequirements(kind) {
  return (KINDS[kind].requires || []).filter(function (needed) {
    var slot = slots[needed];
    return !slot || !slot.enabled.value;
  });
}

Object.keys(KINDS).forEach(function (kind) {
  // Resolved on read, never at registration: plugin files load after this one,
  // so a default picked while registering would depend on script order.
  var plugin = Vue.computed(function () {
    return get(prefs[kind].pluginId) || forKind(kind)[0] || null;
  });

  // What this slot needs and has not got. Also what the settings card says
  // instead of a form, so a switch that would do nothing explains itself
  // rather than merely refusing to move.
  var blocked = Vue.computed(function () { return unmetRequirements(kind); });

  /* Whether this salon is entitled to the slot at all.
   *
   * Kept separate from `blocked` rather than folded into it, because the two
   * are different kinds of "no" and the settings card has to tell them apart: a
   * blocked slot is waiting on a switch the salon can throw, an unlicensed one
   * is waiting on a conversation with us. Read live, like everything else here
   * — a licence granted mid-shift reaches the console on its own.
   */
  var licensed = Vue.computed(function () {
    var feature = KINDS[kind].licence;
    return !feature || licensing.licensed(feature);
  });

  slots[kind] = {
    kind: kind,
    label: KINDS[kind].label,
    plugins: Vue.computed(function () { return forKind(kind); }),
    plugin: plugin,
    provider: Vue.computed(function () { return plugin.value ? plugin.value.provider : null; }),
    requires: (KINDS[kind].requires || []).slice(),
    blocked: blocked,
    licensed: licensed,
    licence: KINDS[kind].licence || null,
    // Switched on AND something registered to do the work AND everything it
    // depends on is itself on AND this salon has bought it. A slot with nothing
    // in it is off however the preference reads — otherwise the UI would offer
    // features with no provider behind them — and so is one whose dependency
    // has gone, and one that is not licensed.
    enabled: Vue.computed(function () {
      return !!(!KINDS[kind].disabled && prefs[kind].enabled && plugin.value &&
                !blocked.value.length && licensed.value);
    }),
    // What the preference says, regardless of whether a plugin exists. The
    // settings toggle binds to this so it does not silently flip itself.
    wanted: Vue.computed(function () { return !!prefs[kind].enabled; }),
    name: Vue.computed(function () { return plugin.value ? plugin.value.name : ''; }),
    can: function (cap) {
      var p = plugin.value;
      return !!(!KINDS[kind].disabled && prefs[kind].enabled && !blocked.value.length && licensed.value &&
                p && p.capabilities && p.capabilities[cap]);
    },
    setEnabled: function (on) {
      prefs[kind].enabled = !!on;
      save();
    },
    setPlugin: function (id) {
      if (!get(id)) return false;
      prefs[kind].pluginId = id;
      save();
      return true;
    }
  };
});

export const plugins = {
  register: register,
  reload: reload,
  get: get,
  list: function () { return registry; },
  slot: function (kind) { return slots[kind] || null; },
  scheduling: slots.scheduling,
  channel: slots.channel
};

/* Settings forms belonging to plugins, keyed by the name a plugin names in its
 * registration (`settings: '<name>-settings'`).
 *
 * It lives here rather than in any one form's file because it is shared: each
 * form adds itself as it is imported, and the shell registers the lot as global
 * components. That indirection is what lets a plugin name a form without
 * importing it — the settings page renders by name and has no business pulling
 * in a form for a system it does not know exists.
 *
 * Mutated rather than reassigned. Every importer holds this same object, so
 * replacing it would strand whoever read it first.
 */
export const pluginSettings = {};
